import React from 'react';
import { Crown, Briefcase, Users, Heart, Baby, AlertTriangle, HeartHandshake } from 'lucide-react';

const CharaKarakaDisplay = ({ jaiminiData }) => {
  // Use 7-karaka system by default (classical standard), user can toggle to 8-karaka
  const [useEightKaraka, setUseEightKaraka] = React.useState(false);

  if (!jaiminiData) return null;
  
  const karakas = useEightKaraka 
    ? (jaiminiData["5.1_chara_karakas_8"] || jaiminiData["5.1_chara_karakas_7"])
    : (jaiminiData["5.1_chara_karakas_7"] || jaiminiData["5.1_chara_karakas_8"]);
  const karakaOrder = useEightKaraka
    ? (jaiminiData["5.1_karaka_order_8"] || jaiminiData["5.1_karaka_order_7"] || ["AK", "AmK", "BK", "MK", "PK", "GK", "DK", "PiK"])
    : (jaiminiData["5.1_karaka_order_7"] || jaiminiData["5.1_karaka_order_8"] || ["AK", "AmK", "BK", "MK", "PiK", "GK", "DK"]);
  const karakamsa = jaiminiData["5.2_karakamsha"];
  const swamsha = jaiminiData["5.2_swamsha"];
  const arudhas = jaiminiData["5.3_arudha_al_ul"];
  const systemUsed = useEightKaraka ? "8-karaka (with Rahu)" : "7-karaka (Classical)";

  if (!karakas) return null;

  // Icon mapping for each Karaka (8-karaka system)
  const karakaIcons = {
    "AK": Crown,
    "AmK": Briefcase,
    "BK": Users,
    "MK": Heart,
    "PK": Baby,  // Putra in 8-karaka
    "PiK": Baby, // Pitru in 8-karaka or Putra in 7-karaka
    "GK": AlertTriangle,
    "DK": HeartHandshake
  };

  // Color mapping for each Karaka
  const karakaColors = {
    "AK": "from-yellow-500 to-orange-500",
    "AmK": "from-blue-500 to-cyan-500",
    "BK": "from-green-500 to-emerald-500",
    "MK": "from-pink-500 to-rose-500",
    "PiK": "from-purple-500 to-violet-500",
    "GK": "from-red-500 to-orange-600",
    "DK": "from-indigo-500 to-blue-500"
  };

  const karakaBorderColors = {
    "AK": "border-yellow-500/50",
    "AmK": "border-blue-500/50",
    "BK": "border-green-500/50",
    "MK": "border-pink-500/50",
    "PiK": "border-purple-500/50",
    "GK": "border-red-500/50",
    "DK": "border-indigo-500/50"
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500 mb-2">
          Chara Karakas
        </h2>
        <p className="text-white/70 text-sm">
          {systemUsed} - Ranked by Degree Within Sign (Highest to Lowest)
        </p>
        <div className="mt-3 flex justify-center items-center gap-3">
          <div className="inline-block bg-yellow-500/20 px-4 py-1 rounded-full border border-yellow-500/50">
            <span className="text-yellow-400 text-xs font-medium">{systemUsed}</span>
          </div>
          <button
            onClick={() => setUseEightKaraka(!useEightKaraka)}
            className="px-4 py-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 text-blue-400 text-sm font-medium transition-all"
          >
            Switch to {useEightKaraka ? "7-karaka" : "8-karaka"}
          </button>
        </div>
        <p className="text-white/50 text-xs mt-2">
          {useEightKaraka 
            ? "8-karaka includes Rahu as Matrukaraka" 
            : "7-karaka: Classical system (Mercury is Darakaraka with lowest degree)"}
        </p>
      </div>

      {/* Karaka Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {karakaOrder.map((role, index) => {
          const karaka = karakas[role];
          if (!karaka) return null;

          const Icon = karakaIcons[role];
          const colorGradient = karakaColors[role];
          const borderColor = karakaBorderColors[role];

          return (
            <div
              key={role}
              className={`bg-gradient-to-br from-slate-900/80 to-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border-2 ${borderColor} shadow-xl hover:scale-105 transition-transform duration-300`}
            >
              {/* Rank Badge */}
              <div className="flex items-center justify-between mb-4">
                <div className={`bg-gradient-to-r ${colorGradient} text-white px-3 py-1 rounded-full text-xs font-bold`}>
                  #{index + 1} {role}
                </div>
                {Icon && <Icon className="w-6 h-6 text-yellow-400" />}
              </div>

              {/* Karaka Details */}
              <div className="space-y-3">
                <h3 className="text-white text-xl font-bold">{karaka.full_name}</h3>
                <p className="text-cyan-400 text-sm italic">{karaka.meaning}</p>

                {/* Planet Info */}
                <div className="bg-white/5 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">Planet:</span>
                    <span className="text-yellow-400 font-bold">{karaka.planet}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">Sign:</span>
                    <span className="text-cyan-300 font-medium">{karaka.sign}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/60 text-sm">Degree:</span>
                    <span className="text-purple-300 font-medium">{karaka.degree_in_sign?.toFixed(2)}°</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Karakamsha & Swamsha Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Karakamsha */}
        {karakamsa && karakamsa.ak_planet && (
          <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 rounded-2xl p-6 border border-purple-500/30">
            <h3 className="text-purple-300 text-xl font-bold mb-4">Karakāṁśa (AK in Navamsa - D9)</h3>
            <div className="space-y-3">
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-white/60 text-sm mb-1">AK Planet</p>
                <p className="text-yellow-400 font-bold text-lg">{karakamsa.ak_planet}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-white/60 text-sm mb-1">D9 Sign</p>
                <p className="text-cyan-400 font-bold text-lg">{karakamsa.d9_sign}</p>
              </div>
              <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/30">
                <p className="text-purple-300 text-sm">Material field of action & spiritual path</p>
              </div>
            </div>
          </div>
        )}

        {/* Swamsha */}
        {swamsha && swamsha.lagna_lord && (
          <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 rounded-2xl p-6 border border-cyan-500/30">
            <h3 className="text-cyan-300 text-xl font-bold mb-4">Swāmśa (Lagna Lord in D9)</h3>
            <div className="space-y-3">
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-white/60 text-sm mb-1">Lagna Lord</p>
                <p className="text-yellow-400 font-bold text-lg">{swamsha.lagna_lord}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-white/60 text-sm mb-1">D9 Sign</p>
                <p className="text-cyan-400 font-bold text-lg">{swamsha.d9_sign}</p>
              </div>
              <div className="bg-cyan-500/10 rounded-lg p-3 border border-cyan-500/30">
                <p className="text-cyan-300 text-sm">{swamsha.meaning}</p>
                <p className="text-white/60 text-xs mt-1 italic">{swamsha.interpretation}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Arudha Lagna & Upapada Section */}
      {arudhas && arudhas.AL_sign && (
        <div className="bg-gradient-to-br from-green-900/30 to-teal-900/30 rounded-2xl p-6 border border-green-500/30">
          <h3 className="text-green-300 text-xl font-bold mb-4">Ārūḍha Lagna (AL) & Upapāda (UL)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Arudha Lagna */}
            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="text-green-400 font-bold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                Ārūḍha Lagna (AL)
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-white/60 text-sm">Sign:</span>
                  <span className="text-cyan-400 font-medium">{arudhas.AL_sign}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60 text-sm">Lagna Sign:</span>
                  <span className="text-yellow-400 font-medium">{arudhas.lagna_sign}</span>
                </div>
                <p className="text-white/50 text-xs mt-2 italic">
                  Image vs Reality - How others perceive you
                </p>
              </div>
            </div>

            {/* Upapada */}
            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="text-teal-400 font-bold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-teal-400 rounded-full"></span>
                Upapāda (UL)
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-white/60 text-sm">Sign:</span>
                  <span className="text-cyan-400 font-medium">{arudhas.UL_sign}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60 text-sm">12th House Sign:</span>
                  <span className="text-purple-400 font-medium">{arudhas.lagna_sign}</span>
                </div>
                <p className="text-white/50 text-xs mt-2 italic">
                  Marriage & Partnership karma
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Interpretations */}
      <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/30 rounded-2xl p-6 border border-white/10">
        <h3 className="text-white text-lg font-bold mb-4">Key Jaimini Interpretations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-start gap-2">
            <span className="text-yellow-400 mt-1">•</span>
            <div>
              <span className="text-cyan-400 font-medium">AK + AmK:</span>
              <span className="text-white/70"> Life mission and soul's purpose</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-yellow-400 mt-1">•</span>
            <div>
              <span className="text-pink-400 font-medium">AK + DK:</span>
              <span className="text-white/70"> Marriage and relationship karma</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-yellow-400 mt-1">•</span>
            <div>
              <span className="text-green-400 font-medium">AL vs Lagna:</span>
              <span className="text-white/70"> Public image vs true self</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-yellow-400 mt-1">•</span>
            <div>
              <span className="text-purple-400 font-medium">Karakāṁśa:</span>
              <span className="text-white/70"> Material field and spiritual growth area</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharaKarakaDisplay;
